import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import fire from "../config/Fire";
import API from "../utils/API";
import { tierFor } from "./permissions";

/**
 * Central auth state for the whole app (REDESIGN-RUNBOOK.md §3.3/§3.5).
 *
 * THE structural fix: Firebase restores a persisted session asynchronously,
 * and the legacy pages fired authenticated requests (or auth checks) before
 * that restore finished — so refreshing any authed page sent tokenless
 * requests, got a 401, and bounced to /login. This provider subscribes ONCE
 * to onAuthStateChanged (the pattern AdminPage shipped) and exposes:
 *
 *   authReady — Firebase has settled (session restored OR definitely absent).
 *               RequireAuth renders nothing auth-dependent until this is true.
 *   user      — the Firebase user (null when signed out).
 *   role      — "client" | "agent" | "admin" | null, resolved by probing the
 *               same login endpoints the legacy LoginCard cascade used.
 *   roleReady — role resolution finished (role may still be null → the uid
 *               maps to no application identity).
 *   identity  — the matching Mongo record (client/agent/admin document).
 *   agents    — the agents list legacy pages take as a prop (fetched once
 *               per session, exactly as the old App.authListener did).
 */

const AuthContext = createContext(null);

/* Probe order is ADMIN-FIRST (admin → agent → client), mirroring the
   backend's attachRole resolution. This matters for dual-role uids (Evan is
   on both an admin and an agent record): highest privilege must win here
   exactly as it does server-side, or an admin lands on the agent surface.
   (The legacy LoginCard probed client-first — deliberately NOT mirrored.)
   Each probe treats any error — including a 403 from the hardened backend,
   which the API layer suppresses from the global toast for these calls — as
   "not this role". */
async function resolveRole(uid) {
  const probes = [
    { role: "admin", request: API.probeAdminLogin },
    { role: "agent", request: API.probeAgentLogin },
    { role: "client", request: API.probeClientLogin }
  ];
  // A 429 (rate limit — the deployed limiter is 100 req/15min/IP and an
  // ordinary session spends several probes + list fetches) is TRANSIENT, not a
  // verdict that the uid maps to no role. Track it separately so a busy moment
  // never masquerades as "this account isn't linked to an agency profile".
  let rateLimited = false;
  let retryAfter = 0;
  for (const probe of probes) {
    try {
      const res = await probe.request(uid);
      if (Array.isArray(res.data) && res.data.length > 0) {
        return { role: probe.role, identity: res.data[0], rateLimited: false, retryAfter: 0 };
      }
    } catch (err) {
      const status = err && err.response && err.response.status;
      if (status === 429) {
        // Contract (Coordinator/Session I): body is
        // { error:"RATE_LIMITED", retryAfter:<seconds>, message } with a numeric
        // Retry-After header. Prefer the body's retryAfter, fall back to the
        // header. Status 429 stays the PRIMARY discriminator so this is correct
        // against the OLD limiter too (no RATE_LIMITED body).
        rateLimited = true;
        const body = (err.response && err.response.data) || {};
        const headers = (err.response && err.response.headers) || {};
        const ra = Number(body.retryAfter != null ? body.retryAfter : headers["retry-after"]);
        if (Number.isFinite(ra) && ra > retryAfter) {
          retryAfter = ra;
        }
      }
      // else: not this role (403 out-of-scope, 404, network…) — try the next.
    }
  }
  return { role: null, identity: null, rateLimited, retryAfter };
}

const SIGNED_OUT_STATE = {
  authReady: true,
  user: null,
  role: null,
  roleReady: true,
  identity: null,
  agents: [],
  rateLimited: false,
  retryAt: null
};

const MAX_ROLE_RETRIES = 3;

export function AuthProvider({ children }) {
  const [state, setState] = useState({
    ...SIGNED_OUT_STATE,
    authReady: false,
    roleReady: false
  });

  // Resolve the role for a uid, with bounded auto-retry when the backend is
  // rate-limiting (429) — so a busy moment recovers on its own instead of
  // stranding the user in a false "not linked to an agency profile" state.
  const loadRole = useCallback((uid, attempt = 0) => {
    resolveRole(uid).then((result) => {
      // Honor Retry-After; clamp to a sane window so a bad value can't wedge us.
      const willRetry = Boolean(result.rateLimited) && attempt < MAX_ROLE_RETRIES;
      const delay = willRetry
        ? Math.min(Math.max((result.retryAfter || 0) * 1000, 4000), 30000)
        : 0;
      const retryAt = willRetry ? Date.now() + delay : null;
      // Ignore a resolution that lands after this uid signed out.
      setState((s) =>
        s.user && s.user.uid === uid
          ? {
              ...s,
              role: result.role,
              identity: result.identity,
              roleReady: true,
              rateLimited: Boolean(result.rateLimited),
              retryAt
            }
          : s
      );
      if (willRetry) {
        window.setTimeout(() => {
          const current = fire.auth().currentUser;
          if (current && current.uid === uid) {
            loadRole(uid, attempt + 1);
          }
        }, delay);
      }
    });
  }, []);

  useEffect(() => {
    const unsubscribe = fire.auth().onAuthStateChanged((user) => {
      if (!user) {
        setState(SIGNED_OUT_STATE);
        return;
      }

      // Session restored (or fresh login): authed pages may mount now; role
      // resolution and the agents fetch continue in the background.
      setState((s) => ({
        ...s,
        authReady: true,
        user,
        roleReady: false,
        rateLimited: false,
        retryAt: null
      }));

      const uid = user.uid;
      loadRole(uid);

      API.getAgents()
        .then((res) => {
          setState((s) => (s.user && s.user.uid === uid ? { ...s, agents: res.data } : s));
        })
        .catch(() => {
          // Non-fatal: pages that need agents handle an empty list.
        });
    });
    return unsubscribe;
  }, [loadRole]);

  // Manual re-probe (the login page's "Try again" when rate-limited).
  const retryRole = useCallback(() => {
    const user = fire.auth().currentUser;
    if (user) {
      setState((s) => ({ ...s, roleReady: false, rateLimited: false, retryAt: null }));
      loadRole(user.uid);
    }
  }, [loadRole]);

  // tier — "admin" | "writing" | "referral" | null (ROLE-DESIGN-2026-07-11):
  // derived from role + the agent record's `authority` field; meaningful once
  // roleReady. UX-only — the backend stays the real boundary.
  const value = useMemo(
    () => ({ ...state, tier: tierFor(state.role, state.identity), retryRole }),
    [state, retryRole]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}

/** Where each role lands after login (until Phase 2 fills /app/today). */
export function roleHome(role) {
  if (role === "admin") {
    return "/admin";
  }
  if (role === "agent") {
    return "/agent";
  }
  if (role === "client") {
    return "/client";
  }
  return "/";
}
